import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "../lib/useReveal";
import { DIRECTORY_LISTINGS, PROGRAM_TYPES, US_STATES, getTypeLabel } from "../data/directory";
import {
    BadgeCheck,
    MapPin,
    Bed,
    ArrowRight,
    Search,
    ShieldCheck,
    SlidersHorizontal,
    Compass,
    X,
} from "lucide-react";

function Badge({ type }) {
    const colors = {
        "sober-living": "bg-emerald-100 text-emerald-700",
        rehab: "bg-rose-100 text-rose-700",
        outpatient: "bg-amber-100 text-amber-700",
        resource: "bg-blue-100 text-blue-700",
    };
    return (
        <span
            className={`text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded ${colors[type]}`}
        >
            {getTypeLabel(type)}
        </span>
    );
}

export function DirectoryCard({ listing, onViewDetails }) {
    return (
        <article
            data-testid={`directory-card-${listing.id}`}
            className="group bg-card border rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary/40 hover:-translate-y-0.5 transition-all flex flex-col"
        >
            <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                <img
                    src={listing.image}
                    alt={listing.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-3 left-3">
                    <Badge type={listing.type} />
                </div>
                {listing.verified && (
                    <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-background/95 backdrop-blur rounded px-2 py-1 text-[11px] font-semibold text-primary border">
                        <BadgeCheck size={12} />
                        Verified
                    </div>
                )}
            </div>
            <div className="p-6 flex flex-col flex-1">
                <h3 className="text-lg font-bold text-foreground leading-snug">{listing.name}</h3>
                <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                        <MapPin size={12} /> {listing.city}, {listing.state}
                    </span>
                    {listing.beds && (
                        <span className="inline-flex items-center gap-1">
                            <Bed size={12} /> {listing.beds} beds
                        </span>
                    )}
                </div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1">
                    {listing.description}
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                    {listing.tags.map((t) => (
                        <span
                            key={t}
                            className="text-[11px] px-2 py-0.5 rounded bg-muted text-muted-foreground font-medium"
                        >
                            {t}
                        </span>
                    ))}
                </div>
                <div className="mt-5 pt-4 border-t flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">Informational only</span>
                    <button
                        type="button"
                        onClick={() => onViewDetails(listing)}
                        data-testid={`directory-card-details-${listing.id}`}
                        className="inline-flex items-center gap-1 text-primary text-sm font-semibold group-hover:gap-2 transition-all"
                    >
                        View details <ArrowRight size={14} />
                    </button>
                </div>
            </div>
        </article>
    );
}

export default function Directory({ mode = "teaser" }) {
    const ref = useReveal();
    const [type, setType] = useState("all");
    const [state, setState] = useState("All States");
    const [query, setQuery] = useState("");
    const [detailListing, setDetailListing] = useState(null);

    useEffect(() => {
        if (!detailListing) {
            return;
        }
        const onKey = (e) => {
            if (e.key === "Escape") {
                setDetailListing(null);
            }
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [detailListing]);

    const filtered = useMemo(() => {
        return DIRECTORY_LISTINGS.filter((l) => {
            const byType = type === "all" || l.type === type;
            const byState = state === "All States" || l.state === state;
            const byQuery =
                !query ||
                l.name.toLowerCase().includes(query.toLowerCase()) ||
                l.city.toLowerCase().includes(query.toLowerCase()) ||
                l.tags.some((t) => t.toLowerCase().includes(query.toLowerCase()));
            return byType && byState && byQuery;
        });
    }, [type, state, query]);

    const visible = mode === "teaser" ? filtered.slice(0, 6) : filtered;
    const isTeaser = mode === "teaser";

    return (
        <section
            id="directory"
            ref={ref}
            data-testid="directory-section"
            className={`py-20 md:py-24 scroll-mt-24 ${isTeaser ? "bg-background" : "bg-background"}`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-12 items-end mb-10 md:mb-12 reveal">
                    <div>
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-5">
                            <Compass className="w-4 h-4" />
                            Treatment Directory
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
                            Private, judgment-free support that connects to real-world care.
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            Browse verified sober living homes, rehabs, outpatient programs, and
                            community resources by location and program type. It&apos;s a natural next
                            step from Hope: private support first, real-world resources whenever you&apos;re ready.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-3 gap-4">
                        {[
                            {
                                label: "Verified listings",
                                value: `${DIRECTORY_LISTINGS.length}+`,
                                body: "Recovery resources curated for trust and clarity.",
                            },
                            {
                                label: "Search privately",
                                value: "No login",
                                body: "No account required to browse the directory.",
                            },
                            {
                                label: "Filter fast",
                                value: "State + type",
                                body: "Find sober living, rehab, outpatient, and more.",
                            },
                        ].map((item) => (
                            <div key={item.label} className="rounded-3xl border bg-card p-5">
                                <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold">
                                    {item.label}
                                </div>
                                <div className="text-2xl font-bold text-foreground mt-2">{item.value}</div>
                                <div className="text-sm text-muted-foreground mt-2 leading-relaxed">
                                    {item.body}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div
                    data-testid="directory-filters"
                    className="reveal bg-card border rounded-[2rem] p-4 md:p-5 mb-8 shadow-sm"
                >
                    <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b">
                        <div className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                            <SlidersHorizontal className="w-4 h-4 text-primary" />
                            Filter resources
                        </div>
                        <div className="text-sm text-muted-foreground">
                            {filtered.length} result{filtered.length === 1 ? "" : "s"} matched
                        </div>
                        <div className="ml-auto inline-flex items-center gap-2 text-xs text-muted-foreground rounded-full bg-muted px-3 py-1.5">
                            <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                            Searches stay private
                        </div>
                    </div>

                    <div className="grid md:grid-cols-12 gap-3">
                        <div className="md:col-span-5 flex h-12 items-center gap-2.5 rounded-2xl bg-muted/60 border border-transparent focus-within:border-primary focus-within:bg-background px-3.5 transition-colors">
                            <Search
                                className="w-4 h-4 shrink-0 text-muted-foreground pointer-events-none"
                                strokeWidth={2.25}
                                aria-hidden
                            />
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search by name, city, program, or keyword..."
                                data-testid="directory-search-input"
                                className="min-w-0 flex-1 h-full bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground"
                            />
                        </div>
                        <div className="md:col-span-4 flex flex-wrap gap-1.5">
                            {PROGRAM_TYPES.map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => setType(t.id)}
                                    data-testid={`filter-type-${t.id}`}
                                    className={`px-3.5 h-9 rounded-full text-xs font-semibold transition-colors ${
                                        type === t.id
                                            ? "bg-primary text-primary-foreground"
                                            : "bg-muted text-muted-foreground hover:text-foreground"
                                    }`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <div className="md:col-span-3">
                            <select
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                                data-testid="directory-state-select"
                                className="w-full h-12 px-4 rounded-2xl bg-muted/60 border border-transparent focus:border-primary focus:bg-background outline-none text-sm text-foreground transition-colors"
                            >
                                {US_STATES.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {visible.length === 0 ? (
                    <div
                        data-testid="directory-empty-state"
                        className="reveal text-center py-16 bg-muted/40 rounded-[2rem] border"
                    >
                        <p className="text-xl font-bold text-foreground">No listings match your filters.</p>
                        <p className="mt-2 text-muted-foreground">
                            Try widening your search — we&apos;re adding new verified programs every week.
                        </p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 reveal">
                        {visible.map((l) => (
                            <DirectoryCard key={l.id} listing={l} onViewDetails={setDetailListing} />
                        ))}
                    </div>
                )}

                {detailListing && (
                    <div
                        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="directory-detail-title"
                    >
                        <button
                            type="button"
                            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                            onClick={() => setDetailListing(null)}
                            aria-label="Close details"
                        />
                        <div className="relative z-10 w-full sm:max-w-2xl max-h-[min(100vh,46rem)] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-card border shadow-2xl">
                            <div className="relative aspect-[16/9] sm:aspect-[2/1] w-full overflow-hidden bg-muted">
                                <img
                                    src={detailListing.image}
                                    alt=""
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute top-4 left-4">
                                    <Badge type={detailListing.type} />
                                </div>
                                {detailListing.verified && (
                                    <div className="absolute top-4 right-4 inline-flex items-center gap-1 bg-background/95 backdrop-blur rounded px-2 py-1 text-xs font-semibold text-primary border">
                                        <BadgeCheck size={12} />
                                        Verified
                                    </div>
                                )}
                            </div>
                            <div className="p-6 sm:p-8">
                                <div className="flex items-start justify-between gap-4">
                                    <h2
                                        id="directory-detail-title"
                                        className="text-2xl font-bold text-foreground leading-tight"
                                    >
                                        {detailListing.name}
                                    </h2>
                                    <button
                                        type="button"
                                        onClick={() => setDetailListing(null)}
                                        className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full border border-border hover:bg-muted transition-colors"
                                        aria-label="Close"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                                <p className="mt-1 text-sm font-medium text-primary">
                                    {getTypeLabel(detailListing.type)}
                                </p>
                                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                                    <span className="inline-flex items-center gap-1.5">
                                        <MapPin size={14} /> {detailListing.city}, {detailListing.state}
                                    </span>
                                    {detailListing.beds != null && (
                                        <span className="inline-flex items-center gap-1.5">
                                            <Bed size={14} /> {detailListing.beds} beds
                                        </span>
                                    )}
                                </div>
                                <p className="mt-4 text-foreground/90 leading-relaxed">
                                    {detailListing.description}
                                </p>
                                <div className="mt-4 flex flex-wrap gap-1.5">
                                    {detailListing.tags.map((t) => (
                                        <span
                                            key={t}
                                            className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground font-medium"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </div>
                                <p className="mt-6 text-xs text-muted-foreground border-t pt-4">
                                    Informational only. Genesis Recovery AI does not endorse
                                    specific providers. Verify licensing and fit before choosing care.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {mode === "teaser" && (
                    <div className="text-center mt-12 reveal">
                        <Link
                            to="/directory"
                            data-testid="browse-full-directory-button"
                            className="inline-flex items-center gap-1.5 h-12 px-6 rounded-full bg-foreground text-background hover:bg-foreground/90 text-base font-semibold transition-colors"
                        >
                            Browse full directory
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <p className="mt-4 text-xs text-muted-foreground">
                            Treatment providers shown are informational only and are not endorsements.
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
}
